import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import ShareRow from "@/components/ShareRow";
import { Reveal, MaskLine, SectionHead } from "@/components/Reveal";
import { IMAGES, SDG_TARGETS, GLOBAL_CITIES } from "@/data/content";

const Vision = () => (
  <>
    <SEO
      title="Vision 2047 — India's Moment, Delhi's Responsibility"
      path="/vision"
      description="India's G20 moment, Viksit Bharat 2047 and the Master Plan for Delhi 2047 converge on one frontier: the ward. Smart Nagar Nigam's vision for Delhi as the world's best-governed capital, aligned with SDG 11 and the world's best-governed cities."
    />
    <section className="relative overflow-hidden pt-28 md:pt-52" data-testid="vision-hero">
      <div className="mx-auto max-w-[1600px] px-5 pb-20 md:px-10 md:pb-28">
        <MaskLine delay={0.1}>
          <span className="text-xs uppercase tracking-[0.2em] sm:tracking-[0.35em] text-gold md:text-sm">Vision 2047</span>
        </MaskLine>
        <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-tight text-ivory">
          <MaskLine delay={0.25} className="text-[11vw] md:text-[7vw]">India's Moment.</MaskLine>
          <MaskLine delay={0.4} className="text-[11vw] md:text-[7vw]">
            <span className="text-outline">Delhi's Responsibility.</span>
          </MaskLine>
        </h1>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="vision-india">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="01" overline="Why This Is India's Moment" title="A Global Power in the Making." />
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal className="space-y-6 text-base leading-relaxed text-white/70 md:text-lg">
            <p>
              India stands at the most consequential juncture in its post-Independence
              history. The world's most populous democracy — having sustained the
              fastest-growing major economy for nearly a decade — is no longer merely an
              emerging power. It is a global power in the making. India's presidency of the
              G20, its leadership in the Global South, the accelerating story of Digital
              Public Infrastructure, and the ambition of Viksit Bharat 2047 all converge to
              create a singular truth: the world is watching India, and India must now perform
              at the standard of its global ambition.
            </p>
            <p>
              Urban India is the engine of that ambition. By 2047, more than half of India's
              population will be urban. Cities and towns already generate over 60 percent of
              India's GDP. The quality of governance in India's urban local bodies is not a
              municipal matter alone — it is a matter of national competitiveness, social
              equity, and sovereign credibility.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="space-y-6 text-base leading-relaxed text-white/70 md:text-lg">
            <p>
              Yet the truth is stark: the last mile of governance — the ward — remains largely
              unmeasured, unaccountable, and invisible to the very citizens it exists to
              serve. Macro achievements — Smart Cities, AMRUT, PM Awas Yojana, Digital India —
              have not always reached the street, the drain, the park, or the grievance window
              at the ward level.
            </p>
            <p className="border-l-2 border-gold pl-6 font-display text-xl font-bold leading-snug text-ivory md:text-2xl">
              Smart Nagar Nigam was built precisely to close that gap. This is India's moment —
              and ward-level governance is the frontier where that moment is either seized or
              lost.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="relative overflow-hidden border-t border-white/10" data-testid="vision-delhi">
      <div className="absolute inset-0">
        <img src={IMAGES.architecture} alt="Delhi 2047 — a city of illuminated towers and civilisational ambition" className="h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-obsidian/88" />
      </div>
      <div className="relative z-10 mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="02" overline="Delhi's Specific Responsibility" title="The Mirror in Which the World Sees India." />
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="space-y-6 text-base leading-relaxed text-white/75 md:text-lg lg:col-span-7">
            <p>
              Delhi is not merely one of India's largest cities. It is India's capital — the
              seat of Parliament, the home of the Central Government, the address of the
              nation's diplomatic identity, and the symbol of what India aspires to be. With
              20 million citizens, 250 wards, and a population that will approach 32 million
              by 2047, Delhi carries a governance responsibility no other city in history has
              borne at this scale.
            </p>
            <p>
              The Union Government's notification of the Master Plan for Delhi 2047 — unveiled
              by Union Housing and Urban Affairs Minister Manohar Lal Khattar — marks a
              historic reaffirmation of Delhi's centrality to India's future. This is not an
              administrative document. It is a statement of civilisational ambition.
            </p>
            <p>
              But a Master Plan is only as powerful as the governance architecture that
              implements it. The ward is where Master Plans either become lived reality or
              remain archived aspiration — and it is at the ward level that Smart Nagar Nigam
              will ensure MPD-2047's vision reaches every street, every citizen, and every
              neighbourhood of Delhi.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="border border-gold/30 bg-black/50 p-8 backdrop-blur-md md:p-10">
              <p className="text-xs uppercase tracking-[0.25em] text-gold">MPD-2047 Envisions</p>
              <ul className="mt-6 space-y-4 text-sm leading-relaxed text-white/75">
                {[
                  "30–40 lakh new housing units",
                  "24-hour city zones",
                  "Air taxis & inland waterways on the Yamuna",
                  "Transit-Oriented Development",
                  "200-km cycling & park connector network",
                  "1,700 hectares of Yamuna floodplain restoration",
                  "Dwarka as an international investment hub",
                  "Technology-driven governance via GIS & digital platforms",
                ].map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-gold" aria-hidden="true" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="section-light border-t border-white/10" data-testid="vision-sdg">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="03" overline="SDG 11 — Sustainable Cities & Communities" title="Seven Targets. One Ward-Level Engine." />
        <p className="mb-12 max-w-3xl text-base leading-relaxed text-white/70 md:text-lg">
          The United Nations' SDG 11 commits the world to making cities inclusive, safe,
          resilient and sustainable by 2030. Smart Nagar Nigam's programme directly addresses
          seven of its specific targets — and embodies SDG 17's call for partnerships.
        </p>
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-3">
          {SDG_TARGETS.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.04} className="bg-coal p-8">
              <p className="font-display text-3xl font-extrabold tracking-tight text-gold">SDG {t.id}</p>
              <p className="mt-4 text-sm leading-relaxed text-white/65">{t.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="vision-global">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="04" overline="Global Benchmarks" title="Learning from the World's Best-Governed Capitals." />
        <p className="mb-12 max-w-3xl text-base leading-relaxed text-white/70 md:text-lg">
          The aspiration to position Delhi as the world's best-governed capital is grounded in
          a rigorous study of governance excellence — adapted for India's federal democratic
          context.
        </p>
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-3">
          {GLOBAL_CITIES.map((c, i) => (
            <Reveal key={c.city} delay={i * 0.05} className="bg-obsidian p-8 md:p-10">
              <h3 className="font-display text-2xl font-bold tracking-tight text-gold">{c.city}</h3>
              <p className="mt-4 text-sm leading-relaxed text-white/70">{c.practice}</p>
              <p className="mt-4 border-t border-white/10 pt-4 text-xs leading-relaxed text-white/50">{c.relevance}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap items-center gap-8">
            <Link to="/mandate" data-testid="vision-mandate-cta" className="inline-flex items-center gap-3 bg-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-obsidian transition-colors duration-300 hover:bg-gold-light">
              The Constitutional Mandate <ArrowRight size={16} />
            </Link>
            <ShareRow title="Vision 2047 — India's Moment, Delhi's Responsibility" path="/vision" />
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Vision;
